import { createApiClient } from '@/api/apiClient';
import { LID_API_BASE_URL } from '@/constants';
import {
  ExportFileParams,
  GetEnrichmentStatus,
  GetLidStats,
  GetNotLidMembers,
  GetOverview,
} from '@/types';
import { downloadBlob, extractFilenameFromHeader } from '@/utils/helpers';

export const lidService = createApiClient({
  baseURL: `${LID_API_BASE_URL}/api`,
});

export const getOverview = async () => {
  const { data } = await lidService.get<GetOverview>('/overview');

  return data.result;
};

export const getLidStats = async () => {
  const { data } = await lidService.get<GetLidStats>('/lid/stats');

  return data.result;
};

export const getNotLidMembers = async () => {
  const { data } = await lidService.get<GetNotLidMembers>(
    '/lid/notlidmembersintoyes',
  );

  return data.result;
};

export const getEnrichmentStatus = async () => {
  const { data } = await lidService.get<GetEnrichmentStatus>(
    '/lid/enrichmentstatus',
  );

  return data.result;
};

/**
 * Downloads an export file from the LID service
 * @param path - API endpoint path for the export
 * @param params - Query parameters for the request
 * @param onProgress - Optional callback for download progress
 * @param onDownloadComplete - Optional callback when download completes successfully
 * @param onError - Optional callback for error handling
 */
export const getExportFile = async ({
  path,
  params = {},
  onProgress,
  onDownloadComplete,
  onError,
}: ExportFileParams): Promise<void> => {
  try {
    const response = await lidService.get<Blob>(path, {
      params,
      responseType: 'blob',
      onDownloadProgress: ({ total, loaded }) => {
        if (typeof onProgress === 'function' && total) {
          onProgress(Math.round((loaded * 100) / total));
        }
      },
    });

    const contentDisposition = response.headers[
      'content-disposition'
    ] as string;

    if (!contentDisposition) {
      throw new Error('Content-Disposition header is missing');
    }

    const filename = extractFilenameFromHeader(contentDisposition);

    downloadBlob(response.data, filename);
  } catch (error) {
    onError?.();
    throw error;
  } finally {
    onDownloadComplete?.();
  }
};
